import { useEffect, useRef, useState } from "react";
import RIcon from "../../../components/RIcon";

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, fromY = 36, className = "" }: {
  children: React.ReactNode; delay?: number; fromY?: number; className?: string;
}) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0px)" : `translateY(${fromY}px)`,
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const steps = [
  {
    step: "01",
    icon: "ri-upload-cloud-2-line",
    title: "上传企业文档",
    desc: "支持 PDF、Word、Excel、PPT 等 50+ 格式，一次批量上传，AI 自动解析文档结构与内容层级。",
    detail: "上传即解析，系统自动识别文档类型、章节结构与关键段落，支持加密 PDF 和扫描件 OCR 识别。",
    image: "https://readdy.ai/api/search-image?query=enterprise%20document%20upload%20interface%20showing%20drag%20and%20drop%20area%20with%20multiple%20file%20formats%20PDF%20Word%20Excel%20icons%20progress%20bar%20uploading%20animation%20clean%20minimal%20white%20dashboard%20medical%20pharmaceutical%20compliance%20documents&width=480&height=320&seq=howitworks_step1&orientation=landscape",
  },
  {
    step: "02",
    icon: "ri-brain-line",
    title: "AI 智能索引",
    desc: "向量化索引引擎自动提取关键实体、建立语义关联，构建专属企业知识图谱，检索精度大幅提升。",
    detail: "采用 RAG 架构，将文档分块嵌入向量数据库，支持跨文档语义检索，检索耗时 &lt;200ms。",
    image: "https://readdy.ai/api/search-image?query=AI%20knowledge%20graph%20visualization%20showing%20connected%20document%20nodes%20with%20teal%20green%20color%20scheme%20medical%20pharmaceutical%20knowledge%20base%20indexing%20processing%20animation%20technology%20abstract%20background%20enterprise%20system&width=480&height=320&seq=howitworks_step2&orientation=landscape",
  },
  {
    step: "03",
    icon: "ri-chat-ai-line",
    title: "智能问答对话",
    desc: "用自然语言提问，AI 从知识库精准召回答案，附带来源文档引用，每一条回答都可追溯验证。",
    detail: "支持上下文多轮对话，专业术语识别准确率 &gt;98%，自动过滤权限外文档，数据安全可控。",
    image: "https://readdy.ai/api/search-image?query=AI%20chat%20interface%20showing%20medical%20professional%20asking%20question%20about%20GMP%20compliance%20ICH%20regulations%20receiving%20detailed%20answer%20with%20document%20source%20citations%20sidebar%20showing%20conversation%20history%20teal%20accent%20colors%20clean%20enterprise%20software&width=480&height=320&seq=howitworks_step3&orientation=landscape",
  },
  {
    step: "04",
    icon: "ri-file-text-line",
    title: "一键生成报告",
    desc: "选择合规模板，填入关键参数，AI 自动生成结构化报告文档，告别重复性手工撰写工作。",
    detail: "预置 CAPA、质量评估、偏差报告等 6 类模板，支持导出 Word/PDF，格式符合监管提交标准。",
    image: "https://readdy.ai/api/search-image?query=document%20generation%20interface%20showing%20template%20selection%20panel%20with%20CAPA%20quality%20report%20options%20on%20left%20and%20generated%20professional%20medical%20compliance%20report%20preview%20on%20right%20clean%20white%20background%20teal%20green%20enterprise%20SaaS&width=480&height=320&seq=howitworks_step4&orientation=landscape",
  },
];



export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section id="how-it-works" className="py-24 bg-[#F4F8FB]">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <Reveal delay={0} fromY={28} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5A0]" />
            <span className="text-[#0EA5A0] text-sm font-medium uppercase tracking-wider">使用流程</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5A0]" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A2B3C] mb-4">四步开启智能知识管理</h2>
          <p className="text-[#718096] text-base max-w-xl mx-auto">
            从文档上传到智能问答，部署当天即可使用，无需繁琐配置
          </p>
        </Reveal>

        {/* Steps Interactive Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
          {/* Step Tabs */}
          <div className="lg:col-span-5 space-y-3">
            {steps.map((step, idx) => (
              <Reveal key={idx} delay={idx * 80} fromY={24}>
                <button
                  onClick={() => setActiveStep(idx)}
                  className={`w-full text-left p-5 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                    activeStep === idx
                      ? "border-[#0EA5A0] bg-white"
                      : "border-transparent bg-white/60 hover:bg-white hover:border-gray-200"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                      activeStep === idx ? "bg-[#0EA5A0] text-white" : "bg-[#F4F8FB] text-[#718096]"
                    }`}>
                      <i className={`${step.icon} text-lg`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold tracking-wider ${activeStep === idx ? "text-[#0EA5A0]" : "text-[#718096]"}`}>
                          STEP {step.step}
                        </span>
                      </div>
                      <h3 className={`text-sm font-semibold mb-1 ${activeStep === idx ? "text-[#1A2B3C]" : "text-[#4A5568]"}`}>
                        {step.title}
                      </h3>
                      <p className="text-xs text-[#718096] leading-relaxed">{step.desc}</p>
                      {activeStep === idx && (
                        <div
                          className="mt-3 pt-3 border-t border-gray-100 text-xs text-[#718096] leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: step.detail }}
                        />
                      )}
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Step Illustration */}
          <div className="lg:col-span-7">
            <Reveal delay={100} fromY={20}>
              <div className="sticky top-24">
                {steps.map((step, idx) => (
                  <div
                    key={idx}
                    style={{
                      opacity: activeStep === idx ? 1 : 0,
                      transform: activeStep === idx ? "translateY(0px) scale(1)" : "translateY(12px) scale(0.98)",
                      transition: "opacity 0.4s ease, transform 0.4s cubic-bezier(0.22,1,0.36,1)",
                      display: activeStep === idx ? "block" : "none",
                    }}
                  >
                    {/* Browser Frame */}
                    <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white">
                      <div className="flex items-center gap-2 px-4 py-3 bg-[#F4F8FB] border-b border-gray-200">
                        <span className="w-3 h-3 rounded-full bg-red-400" />
                        <span className="w-3 h-3 rounded-full bg-amber-400" />
                        <span className="w-3 h-3 rounded-full bg-emerald-400" />
                        <div className="flex-1 mx-4 bg-white rounded-md px-3 py-1 text-xs text-[#718096] border border-gray-200 flex items-center gap-1.5">
                          <RIcon name="lock-line" className="text-emerald-500 text-xs" />
                          app.rhinomed-ai.com / {["upload", "indexing", "chat", "generate"][idx]}
                        </div>
                      </div>
                      <div className="w-full h-72">
                        <img
                          src={step.image}
                          alt={step.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                    {/* Step label */}
                    <div className="flex items-center gap-3 mt-4 px-2">
                      <div className="w-8 h-8 rounded-lg bg-[#0EA5A0] flex items-center justify-center flex-shrink-0">
                        <i className={`${step.icon} text-white text-sm`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1A2B3C]">{step.title}</p>
                        <p className="text-xs text-[#718096]">Step {step.step} / 04</p>
                      </div>
                      {/* progress dots */}
                      <div className="ml-auto flex items-center gap-1.5">
                        {steps.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setActiveStep(i)}
                            className={`rounded-full transition-all cursor-pointer ${
                              i === activeStep ? "w-6 h-2 bg-[#0EA5A0]" : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

      </div>
    </section>
  );
}
